import type { HTMLAttributes, ReactNode } from "react";
import { classNames } from "./utils";
import { useIOSPresentation } from '../lib/web-presentation';
import { IOSDisclosure } from '../ios-web/components';

export interface PageHeaderProps {
  actions?: ReactNode;
  description?: ReactNode;
  eyebrow?: string;
  meta?: ReactNode;
  title: ReactNode;
  iosTitle?: ReactNode;
}

export function PageHeader({ actions, description, eyebrow, meta, title, iosTitle }: PageHeaderProps) {
  const ios = useIOSPresentation();
  return (
    <header className="am-page-header">
      <div className="am-page-header__copy">
        {eyebrow && <p className="am-eyebrow">{eyebrow}</p>}
        <h1>{ios && iosTitle ? iosTitle : title}</h1>
        {description && (ios ? <IOSDisclosure title="About this workspace">{description}</IOSDisclosure>
          : <div className="am-page-header__description">{description}</div>)}
        {meta && <div className="am-page-header__meta">{meta}</div>}
      </div>
      {actions && <div className="am-page-header__actions">{actions}</div>}
    </header>
  );
}

export interface HeroProps extends PageHeaderProps {
  background?: ReactNode;
  centered?: boolean;
  footer?: ReactNode;
}

export function Hero({ actions, background, centered, description, eyebrow, footer, meta, title }: HeroProps) {
  return (
    <section className={classNames("am-hero", centered && "am-hero--centered")}>
      {background}
      <div className="am-hero__content">
        {eyebrow && <p className="am-eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        {description && <div className="am-hero__description">{description}</div>}
        {actions && <div className="am-hero__actions">{actions}</div>}
        {meta && <div className="am-hero__meta">{meta}</div>}
      </div>
      {footer && <div className="am-hero__footer">{footer}</div>}
    </section>
  );
}

export interface PanelProps extends Omit<HTMLAttributes<HTMLElement>, "title"> {
  actions?: ReactNode;
  description?: ReactNode;
  footer?: ReactNode;
  title?: ReactNode;
  tone?: "default" | "muted" | "dark";
  iosDisclosure?: boolean;
}

export function Panel({ actions, children, className, description, footer, title, tone = "default", iosDisclosure, ...props }: PanelProps) {
  const ios = useIOSPresentation();
  if (ios && iosDisclosure) return <section className={classNames('am-panel', `am-panel--${tone}`, className)} {...props}>
    <IOSDisclosure title={title ?? 'More details'}>{description && <p className="am-panel__description">{description}</p>}
      {actions}{children}{footer}</IOSDisclosure>
  </section>;
  return (
    <section className={classNames("am-panel", `am-panel--${tone}`, className)} {...props}>
      {(title || description || actions) && (
        <header className="am-panel__header">
          <div>
            {title && <h2 className="am-panel__title">{title}</h2>}
            {description && <div className="am-panel__description">{description}</div>}
          </div>
          {actions && <div className="am-panel__actions">{actions}</div>}
        </header>
      )}
      <div className="am-panel__body">{children}</div>
      {footer && <footer className="am-panel__footer">{footer}</footer>}
    </section>
  );
}

export interface MetricCardProps extends HTMLAttributes<HTMLElement> {
  detail?: ReactNode;
  icon?: ReactNode;
  label: ReactNode;
  trend?: ReactNode;
  value: ReactNode;
}

export function MetricCard({ className, detail, icon, label, trend, value, ...props }: MetricCardProps) {
  return (
    <article className={classNames("am-metric-card", className)} {...props}>
      <div className="am-metric-card__top">
        <span className="am-metric-card__label">{label}</span>
        {icon && <span className="am-metric-card__icon">{icon}</span>}
      </div>
      <strong className="am-metric-card__value">{value}</strong>
      {(trend || detail) && <div className="am-metric-card__detail">{trend}{detail}</div>}
    </article>
  );
}

export function MetricGrid({ children, className, iosDisclosure, ...props }: HTMLAttributes<HTMLDivElement> & { iosDisclosure?: boolean }) {
  const ios = useIOSPresentation();
  const grid = <div className={classNames("am-metric-grid", className)} {...props}>{children}</div>;
  return ios && iosDisclosure ? <IOSDisclosure title="At a glance">{grid}</IOSDisclosure> : grid;
}

export interface StatusBadgeProps {
  children: ReactNode;
  dot?: boolean;
  tone?: "neutral" | "info" | "success" | "warning" | "danger";
}

export function StatusBadge({ children, dot = true, tone = "neutral" }: StatusBadgeProps) {
  return (
    <span className={`am-status-badge am-status-badge--${tone}`}>
      {dot && <span aria-hidden="true" className="am-status-badge__dot" />}
      <span className="am-status-badge__label">{children}</span>
    </span>
  );
}

export function Cluster({ children, className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={classNames("am-cluster", className)} {...props}>{children}</div>;
}

export function Stack({ children, className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={classNames("am-stack", className)} {...props}>{children}</div>;
}
